import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';

import SharedModal from '../../../shared/_TBComponents/Modal';
import Button from '../../../shared/_TBComponents/Button';
import Icon from '../../../shared/_TBComponents/Icon';
import SharedInput from '../../../shared/_TBComponents/Input';
import SelectBox from '../../../shared/_TBComponents/SelectBox';
import Text from '../../../shared/_TBComponents/Text';
import { formatGoalValue } from '../components/presentational/GoalsCard';
import ConfirmActionModal from './ConfirmActionModal';
import { MAX_TRACKABLE_EXERCISE_TYPES } from '../constants';
import { getMaxEntryFieldConfig } from '../utils/maxesGoalsHelpers';

const GOAL_TYPE = { Value: 'value', Percent: 'percent' };

const GOAL_TYPE_OPTIONS = [
  { id: GOAL_TYPE.Value, name: 'Value' },
  { id: GOAL_TYPE.Percent, name: 'Percent' },
];

const EMPTY_FORM = {
  goalType: GOAL_TYPE.Value, primary1: '', primary2: '', percentage: '', linkedExerciseId: null,
};

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  font-family: "Nunito Sans";
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InheritedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InheritedRow = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isOverridden',
})`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  opacity: ${(props) => (props.isOverridden ? 0.6 : 1)};
  padding: 8px 12px;
  border: 1px dashed ${(props) => props.theme.colors.textTertiary};
  border-radius: 4px;
  font-size: 14px;
  color: ${(props) => props.theme.colors.text};
`;

const GoalInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  & > * {
    flex-shrink: 0;
  }

  input[type='number'] {
    border-color: ${(props) => props.theme.colors.textTertiary};
  }
`;

const UnitLabel = styled.span`
  font-size: 14px;
  color: ${(props) => props.theme.colors.textStrong};
`;

const RemoveButton = styled.button`
  display: flex;
  margin-left: auto;
  padding: 4px;
  border: none;
  background: none;
  line-height: 0;
  color: ${(props) => props.theme.colors.textSecondary};
  cursor: pointer;

  &:hover {
    color: ${(props) => props.theme.colors.text};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 8px;
  border-top: 1px solid ${(props) => props.theme.colors.borderLight};
`;

// Goals are saved as one number so min:sec or ft:in are combined using factor
const getValueFields = (fieldConfig, weightUnitAbbr) => {
  switch (fieldConfig.mode) {
    case 'weightReps':
    case 'weightOnly':
      return { fields: [{ key: 'primary1', unit: weightUnitAbbr }], factor: 1 };
    case 'repsOnly':
      return { fields: [{ key: 'primary1', unit: 'reps' }], factor: 1 };
    case 'timed':
      return { fields: [{ key: 'primary1', unit: 'min' }, { key: 'primary2', unit: 'sec' }], factor: 60 };
    case 'feetInches':
      return { fields: [{ key: 'primary1', unit: 'ft' }, { key: 'primary2', unit: 'in' }], factor: 12 };
    case 'inchesOnly':
      return { fields: [{ key: 'primary1', unit: 'in' }], factor: 1 };
    default:
      return { fields: [{ key: 'primary1', unit: fieldConfig.unitLabel }], factor: 1 };
  }
};

const buildFormFromGoal = (goal, valueFields) => {
  if (!goal) return EMPTY_FORM;
  if (goal.linkedExercise) {
    return {
      ...EMPTY_FORM,
      goalType: GOAL_TYPE.Percent,
      percentage: String(goal.linkedExercise.percentage ?? ''),
      linkedExerciseId: goal.linkedExercise.id,
    };
  }
  const raw = Number(goal.rawValue) || 0;
  if (valueFields.fields.length === 1) return { ...EMPTY_FORM, primary1: String(raw) };
  const primary1 = Math.floor(raw / valueFields.factor);
  return {
    ...EMPTY_FORM,
    primary1: String(primary1),
    primary2: String(Math.round(raw - primary1 * valueFields.factor)),
  };
};

const SetGoalModal = ({
  isOpen, title, goalLabel, exercises, goals, initialExerciseId, weightUnitAbbr,
  isSaving, isDeleting, onSubmit, onDelete, onClose,
}) => {
  const theme = useTheme();
  const [exerciseId, setExerciseId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const selectStyles = useMemo(() => ({
    control: (provided) => ({
      ...provided,
      borderColor: theme.colors.textTertiary,
      '&:hover': { borderColor: theme.colors.textTertiary },
    }),
  }), [theme]);

  const exerciseOptions = useMemo(
    () => exercises
      .filter((exercise) => MAX_TRACKABLE_EXERCISE_TYPES.includes(exercise.type))
      .map((exercise) => ({ id: exercise.id, name: exercise.name, unitAbbr: exercise.unitAbbr })),
    [exercises],
  );

  const selectedExercise = exercises.find((exercise) => exercise.id === exerciseId) || null;
  const selectedExerciseOption = exerciseOptions.find((option) => option.id === exerciseId) || null;
  // Only exercises in the same unit can be linked
  const goalUnitAbbr = selectedExercise?.unitAbbr;
  const linkedExerciseOptions = exerciseOptions.filter((option) => option.id !== exerciseId
    && (!goalUnitAbbr || option.unitAbbr === goalUnitAbbr || option.id === form.linkedExerciseId));
  const selectedLinkedOption = linkedExerciseOptions
    .find((option) => option.id === form.linkedExerciseId) || null;
  const selectedGoalTypeOption = GOAL_TYPE_OPTIONS.find((option) => option.id === form.goalType);
  const valueFields = getValueFields(getMaxEntryFieldConfig(selectedExercise), weightUnitAbbr);

  // The goal that applies plus the goals it overrides
  const appliedGoal = goals.find((goal) => goal.exerciseId === exerciseId) || null;
  const ownGoal = appliedGoal && !appliedGoal.isInherited ? appliedGoal : null;
  const inheritedGoals = appliedGoal
    ? [
      ...(appliedGoal.isInherited ? [appliedGoal] : []),
      ...(appliedGoal.inheritedGoalsInPriorityOrder || []),
    ]
    : [];

  const goalValue = valueFields.factor > 1
    ? Number(form.primary1 || 0) * valueFields.factor + Number(form.primary2 || 0)
    : Number(form.primary1 || 0);
  const isPercent = form.goalType === GOAL_TYPE.Percent;
  const isValid = isPercent
    ? Number(form.percentage) > 0 && Boolean(form.linkedExerciseId)
    : goalValue > 0;

  const resetFormForExercise = (nextExerciseId) => {
    const nextExercise = exercises.find((exercise) => exercise.id === nextExerciseId) || null;
    const nextGoal = goals.find((goal) => goal.exerciseId === nextExerciseId && !goal.isInherited);
    setExerciseId(nextExerciseId);
    setForm(buildFormFromGoal(
      nextGoal,
      getValueFields(getMaxEntryFieldConfig(nextExercise), weightUnitAbbr),
    ));
  };

  const handleExerciseChange = (option) => resetFormForExercise(option ? option.id : null);

  const handleGoalTypeChange = (option) => {
    setForm((prev) => ({ ...prev, goalType: option.id }));
  };

  const handleLinkedExerciseChange = (option) => {
    setForm((prev) => ({ ...prev, linkedExerciseId: option ? option.id : null }));
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    if (!isValid) return;
    onSubmit(isPercent
      ? { exerciseId, linkedExerciseId: form.linkedExerciseId, percentage: Number(form.percentage) }
      : { exerciseId, value: goalValue });
  };

  const exerciseLabel = selectedExercise?.name || 'this exercise';
  const removeGoalMessage = {
    Athlete: `Remove this athlete's ${exerciseLabel} goal? They'll fall back to any group, calendar or All Athletes goal.`,
    Group: `Remove this group's ${exerciseLabel} goal? Its athletes fall back to any All Athletes goal, unless they have their own.`,
    Calendar: `Remove this calendar's ${exerciseLabel} goal? Its athletes fall back to any goal set above it, unless they have their own.`,
    Account: `Remove the All Athletes ${exerciseLabel} goal? Athletes without their own, group or calendar goal will no longer have one.`,
  }[ownGoal?.appliesToType] || `Remove the ${exerciseLabel} goal?`;

  const openDeleteConfirm = () => setIsDeleteConfirmOpen(true);
  const closeDeleteConfirm = () => setIsDeleteConfirmOpen(false);

  const handleConfirmDelete = () => {
    setIsDeleteConfirmOpen(false);
    onDelete(ownGoal.goalId);
  };

  useEffect(() => {
    if (isOpen) {
      resetFormForExercise(initialExerciseId);
      setIsDeleteConfirmOpen(false);
    }
    // Only reset when opened so a refresh doesn't wipe what's typed
  }, [isOpen]);

  const renderValueInputs = () => valueFields.fields.map((field) => (
    <React.Fragment key={field.key}>
      <SharedInput
        inputOnly
        type='number'
        name={field.key}
        value={form[field.key]}
        onChange={handleFieldChange}
        width='90px'
        height='38px'
        styledPropsWrapper={{ maxWidth: '90px' }}
        fontSize='14px'
      />
      {field.unit && <UnitLabel>{field.unit}</UnitLabel>}
    </React.Fragment>
  ));

  const renderPercentInputs = () => (
    <>
      <SharedInput
        inputOnly
        type='number'
        name='percentage'
        value={form.percentage}
        onChange={handleFieldChange}
        width='80px'
        height='38px'
        styledPropsWrapper={{ maxWidth: '80px' }}
        fontSize='14px'
      />
      <UnitLabel>% of</UnitLabel>
      <SelectBox
        options={linkedExerciseOptions}
        value={selectedLinkedOption}
        onChangeFunction={handleLinkedExerciseChange}
        placeholder='Select exercise'
        isClearable={false}
        menuPortalTarget={document.body}
        customStyles={selectStyles}
        width='auto'
        styledProps={{ flex: '1 1 0', minWidth: 0 }}
      />
    </>
  );

  return (
    <>
      <SharedModal
        isOpen={isOpen}
        onRequestClose={onClose}
        customButton
        hideButton
        customMaxWidth='560px'
        customBorderRadius='8px'
        customMinHeight='auto'
      >
        <ModalContent>
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 700 }}>{title}</Text>
          <FieldRow>
            <Text styledTextProps={{ fontSize: '14px', fontWeight: 700 }}>Exercise</Text>
            <SelectBox
              options={exerciseOptions}
              value={selectedExerciseOption}
              onChangeFunction={handleExerciseChange}
              placeholder='Select an exercise'
              isClearable={false}
              menuPortalTarget={document.body}
              customStyles={selectStyles}
            />
          </FieldRow>
          {exerciseId && inheritedGoals.length > 0 && (
            <FieldRow>
              <Text styledTextProps={{ fontSize: '14px', fontWeight: 700 }}>Inherited goals</Text>
              <InheritedList>
                {inheritedGoals.map((goal) => (
                  <InheritedRow
                    key={goal.goalId}
                    isOverridden={goal.goalId !== appliedGoal?.goalId}
                  >
                    <span>{goal.sourceLabel}</span>
                    <span>{formatGoalValue(goal)}</span>
                  </InheritedRow>
                ))}
              </InheritedList>
              {ownGoal && (
                <Text styledTextProps={{ fontSize: '13px', color: '#9E9E9E' }}>
                  {`${goalLabel} takes precedence over inherited goals.`}
                </Text>
              )}
            </FieldRow>
          )}
          {exerciseId && (
            <FieldRow>
              <Text styledTextProps={{ fontSize: '14px', fontWeight: 700 }}>{goalLabel}</Text>
              <GoalInputRow>
                <SelectBox
                  options={GOAL_TYPE_OPTIONS}
                  value={selectedGoalTypeOption}
                  onChangeFunction={handleGoalTypeChange}
                  isClearable={false}
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  customStyles={selectStyles}
                  width='150px'
                />
                {isPercent ? renderPercentInputs() : renderValueInputs()}
                {ownGoal && (
                  <RemoveButton type='button' aria-label='Remove goal' onClick={openDeleteConfirm}>
                    <Icon icon='remove' size={16} />
                  </RemoveButton>
                )}
              </GoalInputRow>
            </FieldRow>
          )}
          <ButtonGroup>
            <Button
              type='button'
              name='Cancel'
              fontSize='14px'
              txtColor='#444444'
              bgColor='#FFF'
              borderColor='#444444'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={onClose}
            />
            <Button
              type='button'
              name={isSaving ? 'Saving...' : 'Save'}
              fontSize='14px'
              txtColor='#FFF'
              bgColor='#444444'
              borderColor='#444444'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              disabled={isSaving || isDeleting || !isValid}
              onClick={handleSubmit}
            />
          </ButtonGroup>
        </ModalContent>
      </SharedModal>

      <ConfirmActionModal
        isOpen={isDeleteConfirmOpen}
        title='Remove Goal'
        message={removeGoalMessage}
        confirmLabel='Remove'
        isBusy={isDeleting}
        overlayBackgroundColor='rgba(0, 0, 0, 0.75)'
        onConfirm={handleConfirmDelete}
        onClose={closeDeleteConfirm}
      />
    </>
  );
};

SetGoalModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string,
  goalLabel: PropTypes.string,
  exercises: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    type: PropTypes.string,
    track: PropTypes.number,
    inputType: PropTypes.string,
    unit: PropTypes.string,
    unitAbbr: PropTypes.string,
    unitName: PropTypes.string,
  })),
  goals: PropTypes.arrayOf(PropTypes.shape({
    goalId: PropTypes.number,
    exerciseId: PropTypes.number,
    isInherited: PropTypes.bool,
    rawValue: PropTypes.number,
    sourceLabel: PropTypes.string,
    linkedExercise: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      percentage: PropTypes.number,
    }),
    inheritedGoalsInPriorityOrder: PropTypes.arrayOf(PropTypes.shape({
      goalId: PropTypes.number,
      sourceLabel: PropTypes.string,
    })),
  })),
  initialExerciseId: PropTypes.number,
  weightUnitAbbr: PropTypes.string,
  isSaving: PropTypes.bool,
  isDeleting: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

SetGoalModal.defaultProps = {
  title: '',
  goalLabel: 'Goal',
  exercises: [],
  goals: [],
  initialExerciseId: null,
  weightUnitAbbr: 'lbs',
  isSaving: false,
  isDeleting: false,
};

export default SetGoalModal;
